import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useUpdateCampaignWizard } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { FormErrors } from 'shared/ui/FormErrors';
import { FormSelect } from 'shared/ui/Select';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { showDefaultErrorToast } from 'shared/lib/notifications';
import type { ServiceType } from 'entities/service';
import { useGetServicesOptions, useGetServicesForFilters } from 'entities/service';

import { useCampaignId, useCreateCampaignStoreActions, useServiceObject } from './CreateCampaignStoreHook';

type StepServiceProps = {
  onBack: () => void;
  onNext: () => void;
};

type FormType = {
  serviceId: string;
};

const getServiceObject = (services: ServiceType[], id: string) => services.find((item) => item.id === Number(id));

const SELECT_NAME = 'serviceId';
const emptyServices = [] as ServiceType[];

export function StepService(props: StepServiceProps) {
  const { onBack, onNext } = props;
  const { updateStepData } = useCreateCampaignStoreActions();
  const serviceObjectFromStore = useServiceObject();
  const campaignId = useCampaignId();
  const updateCampaignQuery = useUpdateCampaignWizard(campaignId);
  const methods = useForm<FormType>({
    defaultValues: {
      serviceId: serviceObjectFromStore?.id.toString(),
    },
  });
  const { handleSubmit, watch } = methods;
  const services = useGetServicesForFilters().data?.data || emptyServices;
  const servicesOptions = useGetServicesOptions();

  const fieldValues = watch();
  const [serviceObject, setServiceObject] = useState<ServiceType | undefined>(serviceObjectFromStore);
  const serviceIdFieldValue = fieldValues[SELECT_NAME];
  const isServiceSelected = Boolean(serviceObject);

  useEffect(() => {
    const newServiceObject = getServiceObject(services, serviceIdFieldValue);

    if (!newServiceObject) {
      return;
    }

    setServiceObject(newServiceObject);
  }, [serviceIdFieldValue, services]);

  const onSubmit = async () => {
    updateCampaignQuery.reset();
    updateStepData({ serviceObject });

    const newServiceId = serviceObject?.id;

    try {
      await updateCampaignQuery.mutateAsync({ payload: { serviceId: newServiceId } });
      onNext();
    } catch (e) {
      showDefaultErrorToast();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-138">
          <div className="text-center text-xl font-medium">Select a home improvement service</div>
          <div className="mt-8 flex justify-center">
            <FormSelect
              isRequired
              title="Select service (cannot be changed later)"
              name={SELECT_NAME}
              wrapperClass="w-full"
              options={servicesOptions}
              selectedOptionId={serviceObject?.id.toString()}
              isLightPlaceholder
              {...getDataAutoTestAttributes(['select-create-wizard-service'])}
            />
          </div>
          <div className="mt-8 flex justify-between">
            <Button
              variant="outline-second"
              type="button"
              onClick={onBack}
              {...getDataAutoTestAttributes(['button-create-wizard-back'])}
            >
              Back
            </Button>
            <Button
              type="submit"
              loading={updateCampaignQuery.isLoading}
              disabled={!isServiceSelected}
              {...getDataAutoTestAttributes(['button-create-wizard-next'])}
            >
              Next
            </Button>
          </div>
        </div>
        <FormErrors error={updateCampaignQuery.error} />
      </form>
    </FormProvider>
  );
}
